import * as Sentry from '@sentry/browser';
import { Severity } from '@sentry/browser';
import { getTenant, getUserId } from './storageUtils';

const TIMEOUT_MS = 5000;

interface IframeState {
  createdAt: number;
  loadedAt: number | null;
  loadErrored: boolean;
  iframeSuccessReceived: boolean;
  anyPostmessageReceived: boolean;
  firstPostmessageOrigin: string | null;
  postmessageCount: number;
  observedEventCodes: string[];
  timeoutHandle: ReturnType<typeof setTimeout> | null;
  src: string;
}

let state: IframeState | null = null;

const breadcrumb = (message: string, data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    category: 'aa-iframe',
    message,
    level: Severity.Info,
    data,
  });
};

const safeHost = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return 'unknown';
  }
};

const baseScope = (scope: Sentry.Scope, outcome: string) => {
  scope.setTag('telemetry', 'aa_iframe');
  scope.setTag('tenant', getTenant() || 'unknown');
  scope.setTag('userId', getUserId());
  scope.setTag('outcome', outcome);
  scope.setFingerprint([outcome, getTenant() || 'unknown']);
};

export const startIframeTracking = (src: string) => {
  if (state && state.timeoutHandle) clearTimeout(state.timeoutHandle);

  state = {
    createdAt: Date.now(),
    loadedAt: null,
    loadErrored: false,
    iframeSuccessReceived: false,
    anyPostmessageReceived: false,
    firstPostmessageOrigin: null,
    postmessageCount: 0,
    observedEventCodes: [],
    timeoutHandle: null,
    src,
  };

  breadcrumb('iframe_created', { srcHost: safeHost(src) });

  state.timeoutHandle = setTimeout(() => {
    if (!state || state.iframeSuccessReceived) return;

    Sentry.withScope((scope) => {
      baseScope(scope, 'iframe_success_timeout');
      scope.setLevel(Severity.Error);
      scope.setTag('iframe_loaded', state!.loadedAt != null ? 'true' : 'false');
      scope.setTag(
        'iframe_load_errored',
        state!.loadErrored ? 'true' : 'false'
      );
      scope.setTag(
        'any_postmessage_received',
        state!.anyPostmessageReceived ? 'true' : 'false'
      );
      scope.setContext('iframe_lifecycle', {
        timeoutMs: TIMEOUT_MS,
        loadElapsedMs: state!.loadedAt
          ? state!.loadedAt - state!.createdAt
          : null,
        postmessageCount: state!.postmessageCount,
        firstPostmessageOrigin: state!.firstPostmessageOrigin,
        observedEventCodes: state!.observedEventCodes,
        srcHost: safeHost(state!.src),
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      });
      Sentry.captureMessage('iframe_success_timeout', Severity.Error);
    });
  }, TIMEOUT_MS);
};

export const markIframeLoaded = () => {
  if (!state || state.loadedAt != null) return;
  state.loadedAt = Date.now();
  breadcrumb('iframe_loaded', {
    elapsedMs: state.loadedAt - state.createdAt,
  });
};

export const markIframeLoadError = () => {
  if (!state) return;
  state.loadErrored = true;
  breadcrumb('iframe_load_error', {
    elapsedMs: Date.now() - state.createdAt,
  });
  Sentry.withScope((scope) => {
    baseScope(scope, 'iframe_load_error');
    scope.setLevel(Severity.Error);
    scope.setContext('iframe_lifecycle', {
      elapsedMs: Date.now() - state!.createdAt,
      srcHost: safeHost(state!.src),
      userAgent:
        typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    });
    Sentry.captureMessage('iframe_load_error', Severity.Error);
  });
};

export const markPostmessageReceived = (origin: string, data: any) => {
  if (!state) return;
  state.anyPostmessageReceived = true;
  state.postmessageCount++;
  if (!state.firstPostmessageOrigin) state.firstPostmessageOrigin = origin;

  const eventCode =
    data && typeof data === 'object' ? data.eventCode : undefined;
  if (eventCode && !state.observedEventCodes.includes(eventCode)) {
    state.observedEventCodes.push(eventCode);
  }

  const dataKeys =
    data && typeof data === 'object'
      ? Object.keys(data).slice(0, 10)
      : [`(${typeof data})`];

  breadcrumb('postmessage_received', {
    origin,
    eventCode: eventCode || '(none)',
    dataKeys,
    count: state.postmessageCount,
    elapsedMs: Date.now() - state.createdAt,
  });
};

export const markIframeSuccess = () => {
  if (!state || state.iframeSuccessReceived) return;
  state.iframeSuccessReceived = true;
  breadcrumb('iframe_success_handled', {
    elapsedMs: Date.now() - state.createdAt,
  });
  if (state.timeoutHandle) {
    clearTimeout(state.timeoutHandle);
    state.timeoutHandle = null;
  }
};

export const stopIframeTracking = () => {
  if (!state) return;
  if (state.timeoutHandle) clearTimeout(state.timeoutHandle);
  state = null;
};
